import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Terminal, FolderOpen, Trash2, FileCode2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import CodeRunner from "@/components/CodeRunner";
import AdBanner from "@/components/AdBanner";

const LANGS = [
  { id: "python", label: "Python", starter: 'print("Hallo Welt!")\nfor i in range(3):\n    print("Zähler:", i)' },
  { id: "javascript", label: "JavaScript", starter: 'console.log("Hallo Welt!");\n[1, 2, 3].forEach(n => console.log(n * n));' },
  { id: "html", label: "HTML / CSS", starter: '<style>\n  body { font-family: sans-serif; text-align: center; }\n  h1 { color: #FF5C00; }\n</style>\n<h1>Hallo Web!</h1>\n<p>Ändere mich 👇</p>' },
  { id: "cpp", label: "C++", starter: '#include <iostream>\nint main() {\n    std::cout << "Hallo Welt!";\n    return 0;\n}' },
  { id: "java", label: "Java", starter: 'public class Main {\n    public static void main(String[] args) {\n        System.out.println("Hallo Welt!");\n    }\n}' },
  { id: "php", label: "PHP", starter: '<?php\necho "Hallo Welt!\\n";\nfor ($i = 0; $i < 3; $i++) {\n    echo "Zähler: $i\\n";\n}' },
  { id: "bash", label: "Linux / Bash", starter: 'echo "Hallo Welt!"\nfor i in 1 2 3; do\n  echo "Zähler: $i"\ndone' },
  { id: "powershell", label: "Windows / PowerShell", starter: 'Write-Host "Hallo Welt!"\nfor ($i = 0; $i -lt 3; $i++) {\n    Write-Host "Zähler: $i"\n}' },
  { id: "xml", label: "XML", starter: '<?xml version="1.0" encoding="UTF-8"?>\n<personen>\n  <person>\n    <name>Mara</name>\n    <alter>25</alter>\n  </person>\n</personen>' },
  { id: "pearl", label: "PEARL", starter: "MODULE pearl_demo;\nPROBLEM hallo;\n\nSPC main: TASK;\nBEGIN\n   PUT 'Hallo Welt!' TO terminal;\nEND;\n\nMODEND;" },
  { id: "ki", label: "KI-Prompting", starter: 'Schreibe hier einen Prompt und überlege:\n- Welche Rolle/Persona soll die KI einnehmen?\n- Welchen Kontext braucht sie?\n- Welches Format soll die Antwort haben?\n\nBeispiel:\n"Erkläre mir Rekursion so, als wäre ich 12 Jahre alt. Nutze ein Beispiel aus dem Alltag."' },
];

export default function Playground() {
  const [lang, setLang] = useState(LANGS[0]);
  const [code, setCode] = useState(LANGS[0].starter);
  const [sessionKey, setSessionKey] = useState(0);
  const [snippets, setSnippets] = useState([]);

  const loadSnippets = () => {
    api.get("/snippets").then((r) => setSnippets(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadSnippets();
    const handler = () => loadSnippets();
    window.addEventListener("snippet-saved", handler);
    return () => window.removeEventListener("snippet-saved", handler);
  }, []);

  const selectLang = (l) => {
    setLang(l);
    setCode(l.starter);
    setSessionKey((k) => k + 1);
  };

  const loadSnippet = (s) => {
    const target = LANGS.find((l) => l.id === s.language) || LANGS[0];
    setLang(target);
    setCode(s.code);
    setSessionKey((k) => k + 1);
    toast.success(`Snippet "${s.title}" geladen`);
  };

  const deleteSnippet = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/snippets/${id}`);
      setSnippets((prev) => prev.filter((s) => s.id !== id));
      toast.success("Snippet gelöscht");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="playground-page">
      <div className="animate-fade-up">
        <div className="flex items-center gap-2">
          <Terminal className="h-7 w-7 text-primary" strokeWidth={2.5} />
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Playground</h1>
        </div>
        <p className="text-muted-foreground font-medium mt-1">
          Experimentiere frei mit Code. Python, C++, Java, PHP &amp; Bash werden auf dem Server ausgeführt.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {LANGS.map((l) => (
          <button
            key={l.id}
            onClick={() => selectLang(l)}
            data-testid={`playground-lang-${l.id}`}
            className={`font-bold text-sm px-4 py-2 rounded-lg border-2 border-black transition-all ${
              lang.id === l.id ? "bg-primary text-white nb-shadow-sm" : "bg-white nb-press"
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2">
          <CodeRunner key={sessionKey} language={lang.id} initialCode={code} />
        </div>

        <div className="space-y-4">
          <div className="nb-card p-5" data-testid="snippets-panel">
            <div className="flex items-center gap-2 mb-4">
              <FolderOpen className="h-5 w-5 text-secondary" strokeWidth={2.5} />
              <h3 className="font-heading font-black text-lg">Meine Snippets</h3>
            </div>
            {snippets.length === 0 ? (
              <p className="text-sm text-muted-foreground font-medium">
                Noch keine gespeicherten Snippets. Klicke im Editor auf das Disketten-Symbol, um Code zu speichern.
              </p>
            ) : (
              <div className="space-y-2">
                {snippets.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => loadSnippet(s)}
                    data-testid={`snippet-${s.id}`}
                    className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg border-2 border-black bg-white nb-press"
                  >
                    <FileCode2 className="h-4 w-4 shrink-0 text-primary" strokeWidth={2.5} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{s.title}</div>
                      <div className="text-[11px] font-medium text-muted-foreground uppercase">{s.language}</div>
                    </div>
                    <span
                      onClick={(e) => deleteSnippet(e, s.id)}
                      data-testid={`snippet-delete-${s.id}`}
                      className="text-muted-foreground hover:text-error p-1"
                      title="Löschen"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={2.5} />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <AdBanner field="playground_snippet" className="nb-card p-4 min-h-[180px] w-full" />
        </div>
      </div>
    </div>
  );
}
